import type { DocumentNode, OperationVariables } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import { useCallback, useEffect, useRef } from "react";

import { isFresh, markFetched } from "@/hooks/useConnectionSetup";

/** How long the pointer must rest before a preload is worth issuing. */
const HOVER_INTENT_MS = 150;

export type PrefetchDescriptor = {
  query: DocumentNode;
  variables: OperationVariables;
} | null;

/**
 * Start the destination's query while the pointer is still on its way there.
 *
 * Every hover fetches. There is no freshness window on top of that: a preload
 * whose data has already landed should still refresh on the next hover. The
 * only request that does not go out is one Apollo is already running: its
 * query deduplication folds an identical in-flight operation into the pending
 * one, which is also what lets the click reuse the preload rather than
 * starting a second copy of it.
 *
 * The result is written to the cache, because a preload that finishes before
 * the click has to leave something behind — deduplicating onto an in-flight
 * request only helps while the request is still running. A hover inside the
 * freshness window does not fetch at all — the data is already there. Writing does mean a
 * broadcast to any mounted query watching that entry, which would repaint the
 * table the user is currently reading, so ``connectionPrefetchDescriptor``
 * declines to preload the view and tab that is already open. Every other
 * entry has no mounted watcher, so the write is invisible until navigation.
 *
 * ``getDescriptor`` runs on the hover-intent tick rather than every render, so
 * it can read live store state and return ``null`` when there is nothing to
 * fetch. Spread the returned props onto the hoverable element.
 */
export function useHoverPrefetch(getDescriptor: () => PrefetchDescriptor) {
  // ``client.query`` rather than ``useLazyQuery``: a preload renders nothing,
  // and a hook result would re-render the element on every response.
  const client = useApolloClient();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const getDescriptorRef = useRef(getDescriptor);
  getDescriptorRef.current = getDescriptor;

  const cancel = useCallback(() => {
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => cancel, [cancel]);

  const onMouseEnter = useCallback(() => {
    if (timeoutRef.current != null) {
      return;
    }
    // The dwell is the only throttle: sweeping the pointer across the sidebar
    // must not fire a query for every item it crosses.
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      const descriptor = getDescriptorRef.current();
      if (!descriptor) {
        return;
      }
      // Fetched inside the freshness window: there is nothing to do, so do
      // nothing. Handing this to Apollo as ``cache-first`` is not the same
      // promise — that only *prefers* the cache, and silently falls through to
      // the network whenever the entry cannot be read in full. Entries here go
      // incomplete routinely: the ``objectsChanged`` subscription writes
      // partial payloads and evicts on delete. So a hover that should have
      // been free still hit the network. Gating the request on the window
      // makes the freshness window mean what it says.
      if (isFresh(descriptor.query, descriptor.variables)) {
        return;
      }
      void client
        .query({ ...descriptor, fetchPolicy: "network-only" })
        // Record the fetch so the click that follows reads the cache instead
        // of going back to the network for something we just fetched.
        .then(() => markFetched(descriptor.query, descriptor.variables))
        // A failed preload is not the user's problem: they will see the real
        // error from the real query if they actually navigate.
        .catch(() => undefined);
    }, HOVER_INTENT_MS);
  }, [client]);

  return { onMouseEnter, onMouseLeave: cancel };
}
